import UseContactUs, { ShowGlobalErrorFn } from '@/hooks/UseContactUs';
import classNames from './ContactUsDrawer.module.scss';
import classes from 'classnames';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { Transition } from 'react-transition-group';

import AppLoaderCircle from '../AppLoaderCircle';
import AppIcon from '@/components/school-management/AppIcon/AppIcon';
import SectionSeperator from '../SectionSeperator/SectionSeperator';
import TheButton from '../TheButton/TheButton';
import AppInput from '../AppInput/AppInput';

export type ContactUsDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  showGlobalError?: ShowGlobalErrorFn;
};

const ContactUsDrawer = ({ isOpen, onClose, showGlobalError }: ContactUsDrawerProps) => {
  const { t } = useTranslation();
  const [showError, setShowError] = useState(false);
  const fieldsScrollRef = useRef<HTMLDivElement>(null);
  const drawerPanelRef = useRef<HTMLDivElement>(null);

  const {
    isLoading,
    ContactUsSchema,
    InitialContactUsValues,
    showMessageSent,
    onCloseMessageSent,
    OnContactUsSubmitted,
  } = UseContactUs({ onCloseDrawer: onClose, showGlobalError });

  const formik = useFormik({
    initialValues: InitialContactUsValues,
    validationSchema: ContactUsSchema,
    onSubmit: OnContactUsSubmitted,
  });

  const shouldDisable =
    !formik.values.subject?.trim() ||
    !formik.values.email?.trim() ||
    !formik.values.message?.trim();

  useEffect(() => {
    if (!isOpen) {
      setShowError(false);
      formik.resetForm();
    }
    const frame = requestAnimationFrame(() => {
      for (const el of [drawerPanelRef.current, fieldsScrollRef.current]) {
        if (el) {
          el.scrollTop = 0;
          el.scrollLeft = 0;
        }
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  return (
    <div className={classNames.drawerShell}>
      <button
        type='button'
        className={classNames.closeBtn}
        onClick={onClose}
        aria-label={t('closeMenu') ?? ''}
      >
        <AppIcon icon='close' />
      </button>
      <div ref={drawerPanelRef} className={classNames.contactUsDrawer}>
        {isLoading ? (
          <AppLoaderCircle className='m-auto' />
        ) : (
          <>
            <div className={classNames.top}>
              <h2>{t('contactUs')}</h2>
              <span>{t('contactUsSubtitle')}</span>
            </div>
            <SectionSeperator className='my-8' />
            <div className={classNames.body}>
              <form
                noValidate
                onSubmit={event => {
                  if (shouldDisable) {
                    return;
                  }
                  setShowError(true);
                  formik.handleSubmit(event);
                }}
              >
                <div ref={fieldsScrollRef} className={classNames.fields}>
                  <AppInput
                    className={classNames.field}
                    inputWrapperClassName={classNames.inputWrapperClassName}
                    id='subject'
                    name='subject'
                    label={t('subject') ?? ''}
                    isRequired
                    placeholder={t('yourSubjectHere') ?? ''}
                    value={formik.values.subject}
                    error={showError ? formik.errors.subject : ''}
                    emitChange={formik.handleChange}
                  />
                  <AppInput
                    className={classNames.field}
                    inputWrapperClassName={classNames.inputWrapperClassName}
                    id='email'
                    name='email'
                    type='email'
                    label={t('email') ?? ''}
                    isRequired
                    placeholder={t('exampleEmail') ?? ''}
                    value={formik.values.email}
                    error={showError ? formik.errors.email : ''}
                    emitChange={formik.handleChange}
                  />
                  <AppInput
                    className={classNames.field}
                    inputWrapperClassName={classNames.inputWrapperClassName}
                    id='phone'
                    name='phone'
                    type='phone'
                    label={t('phone') ?? ''}
                    placeholder={t('yourPhoneHere') ?? ''}
                    value={formik.values.phone}
                    error={showError ? formik.errors.phone : ''}
                    emitChange={formik.handleChange}
                  />
                  <div className={classNames.messageField}>
                    <label htmlFor='message'>*{t('message')}</label>
                    <textarea
                      name='message'
                      id='message'
                      placeholder={t('yourMessageHere') ?? ''}
                      value={formik.values.message}
                      onChange={formik.handleChange}
                    ></textarea>
                  </div>
                </div>

                <div className={classNames.buttons}>
                  <TheButton
                    className={classNames.cancelBtn}
                    text={t('cancel')}
                    plain
                    emitOnClick={onClose}
                  />
                  <TheButton
                    className={classNames.sendBtn}
                    text={t('send')}
                    type='submit'
                    disabled={shouldDisable}
                    shouldRecolor={false}
                  />
                </div>
              </form>
            </div>

            <Transition in={showMessageSent} timeout={400} mountOnEnter unmountOnExit>
              {state => (
                <div className={classNames.messageSentOverlay}>
                  <div className={classes(classNames.messageSentBackdrop, `backdrop-${state}`)} />
                  <div className={classes(classNames.messageSent, `fade-${state}`)}>
                    <h2>{t('thankYou')}</h2>
                    <span>{t('yourMessageHasBeenSentSuccessfully')}</span>
                    <TheButton
                      className={classNames.btn}
                      text={t('done')}
                      emitOnClick={onCloseMessageSent}
                      shouldRecolor={false}
                    />
                  </div>
                </div>
              )}
            </Transition>
          </>
        )}
      </div>
    </div>
  );
};

export default ContactUsDrawer;
